import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import useBrevoEvent from '../utils/useBrevoEvent';
import { useAutomaticCoupon } from './useAutomaticCoupon';
import { useCart, getBestProductImage } from './useCart';
import useCartPriceRevalidation from './useCartPriceRevalidation';
import useProductRules from './useProductRules';
import { getCustomerEmail } from '../utils/customerEmail';
import { getDeliveryComuna, saveDeliveryComuna } from '../utils/deliveryComuna';
import { addSourceFromPage, identifyUser, resetMixpanel, trackAddedToCart, trackLogin } from '../utils/mixpanel';
import { saveStoredUser } from '../utils/userStorage';

const getLocalStorageItem = (key) => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(key);
  }
  return null;
};

const dataProductsCart = getLocalStorageItem("products_cart");
const dataBillingCheckout = getLocalStorageItem("billing_checkout");
const dataUserLogin = getLocalStorageItem("user_login");
const dataCouponCode = getLocalStorageItem("all_data_coupon");
const dataCartSummary = getLocalStorageItem("cart_summary");

// Función para limpiar objetos vacíos del carrito
const cleanCartData = (cartData) => {
  if (!Array.isArray(cartData)) return [];
  return cartData.filter(product => 
    product && 
    product.product_id && 
    product.name && 
    typeof product === 'object' &&
    Object.keys(product).length > 0
  );
};

const normalizeCartSummary = (cartSummary) => {
  if (!cartSummary || typeof cartSummary !== 'object') {
    return null;
  }

  return {
    applied_product_rules: Array.isArray(cartSummary.applied_product_rules) ? cartSummary.applied_product_rules : [],
    discount_from_rules: Number(cartSummary.discount_from_rules || 0),
    total_after_discount: cartSummary.total_after_discount ?? cartSummary.total ?? 0,
    free_shipping_from_rules: cartSummary.free_shipping_from_rules ?? false,
    total: cartSummary.total ?? 0,
    subtotal: cartSummary.subtotal ?? cartSummary.total ?? 0,
  };
};

const initialState = {
  cart: cleanCartData(JSON.parse(dataProductsCart) ?? []),
  product: [],
  billingCheckout: JSON.parse(dataBillingCheckout) ?? [],
  user: JSON.parse(dataUserLogin) ?? null,
  shippingLines: {},
  couponData: JSON.parse(dataCouponCode) ?? [],
  couponDiscount: 0,
  productSearched: [],
  billings: [],
  cartSummary: normalizeCartSummary(JSON.parse(dataCartSummary) ?? null),
  checkoutData: [], // Añadir checkoutData para manejar la información del checkout
  cartShake: false, // Estado para la animación de shake del carrito
  // Comuna de la dirección elegida en el checkout (task-1379): decide las reglas
  // de productos segmentadas por comuna.
  deliveryComuna: getDeliveryComuna(),
};


const useInitialState = () => {
  const [state, setState] = useState(initialState);
  const [cartModal, setCartModal] = useState({ payload: false });
  const [menuMobile, setMenuMobile] = useState({ payload: false });
  const [loginModal, setLoginModal] = useState({ payload: false });
  const [registerModal, setRegisterModal] = useState({ payload: false });
  const [loading, setLoading] = useState(true);
  const cartShakeTimerRef = useRef(null);

  // Obtener el usuario del estado
  const user = state.user;
  const isAuthenticated = !!user;
  const { sendCartUpdatedEvent, sendCartDeletedEvent } = useBrevoEvent(isAuthenticated, user);

  const { applyAutomaticCoupon, validateAndRemoveCoupon, applyAutomaticCouponImmediate, applyAutomaticCouponFrontendOnly } = useAutomaticCoupon();
  
  // Hook del carrito persistente del backend
  const { 
    cart: backendCart, 
    loading: cartLoading,
    addToCart: addToCartBackend,
    updateQuantity: updateQuantityBackend,
    removeFromCart: removeFromCartBackend,
    clearCart: clearCartBackend,
    loadCart: loadCartBackend,
    isAuthenticated: isCartAuthenticated
  } = useCart();

  // Revalidación de precios del carrito (fuente de verdad para NO logueados).
  const revalidatePrices = useCartPriceRevalidation();

  // Reglas de productos para NO logueados (task-1379). Para logueados el resumen
  // llega dentro del carrito del backend (más abajo), así que acá se desactiva
  // para no pisarlo con una segunda fuente.
  const guestRulesSummary = useProductRules({
    cart: state.cart,
    // Solo se lee para invitados: para logueados el resumen ya viene del backend
    // y no vale la pena tocar localStorage en cada render del provider.
    email: isAuthenticated ? null : getCustomerEmail(),
    comuna: state.deliveryComuna,
    enabled: !isAuthenticated,
  });

  /**
   * Registra la comuna de despacho elegida en el checkout (task-1379).
   *
   * Las reglas por comuna dependen de a dónde va el pedido: al cambiar de
   * dirección hay que volver a preguntarle al backend. El invitado lo hace solo
   * (useProductRules tiene la comuna en su clave); el logueado recarga el
   * carrito, que trae el resumen de reglas (ver efecto de abajo).
   */
  const setDeliveryComuna = useCallback((comuna) => {
    const nueva = (typeof comuna === 'string' ? comuna.trim() : '') || null;
    saveDeliveryComuna(nueva);
    setState((prevState) => (
      prevState.deliveryComuna === nueva ? prevState : { ...prevState, deliveryComuna: nueva }
    ));
  }, []);

  // Vuelca el resumen de reglas del invitado al estado global, con la misma forma
  // que el del backend, para que el sidecart, el checkout y el resumen de compra
  // lean de un solo lugar. Se compara antes de escribir: setState en cada render
  // dispararía un ciclo.
  useEffect(() => {
    if (isAuthenticated) return;

    const normalizado = normalizeCartSummary(guestRulesSummary);
    setState((prevState) => {
      if (JSON.stringify(prevState.cartSummary) === JSON.stringify(normalizado)) {
        return prevState;
      }
      if (typeof localStorage !== 'undefined') {
        if (normalizado) {
          localStorage.setItem('cart_summary', JSON.stringify(normalizado));
        } else {
          localStorage.removeItem('cart_summary');
        }
      }
      return { ...prevState, cartSummary: normalizado };
    });
  }, [guestRulesSummary, isAuthenticated]);

  /**
   * Revalida los precios del carrito contra la API y sincroniza el estado global.
   * Para logueados el backend (useCart) ya trae precios frescos + price_changed, así
   * que acá se omite para no duplicar/pisar. Para no logueados es la única forma de
   * actualizar precios en el sidecart, endcart, checkout y payment.
   */
  const revalidateCartPrices = useCallback(async () => {
    if (isAuthenticated) return null; // el backend cart es la fuente de verdad
    const res = await revalidatePrices();
    if (res && res.changed && Array.isArray(res.cart)) {
      setState(prevState => ({ ...prevState, cart: res.cart }));
    }
    return res;
  }, [isAuthenticated, revalidatePrices]);

  // Al montar la app, revalidar precios del carrito para no logueados.
  useEffect(() => {
    revalidateCartPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función para disparar la animación de shake del carrito
  const triggerCartShake = useCallback(() => {
    // Limpiar timer anterior si existe
    if (cartShakeTimerRef.current) {
      clearTimeout(cartShakeTimerRef.current);
    }
    
    setState(prevState => ({
      ...prevState,
      cartShake: true,
    }));
    
    cartShakeTimerRef.current = setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        cartShake: false,
      }));
      cartShakeTimerRef.current = null;
    }, 500);
  }, []);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (cartShakeTimerRef.current) {
        clearTimeout(cartShakeTimerRef.current);
      }
    };
  }, []);

  // Función para limpiar el carrito de objetos vacíos
  const cleanCart = useCallback(() => {
    const currentCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    const cleanedCart = cleanCartData(currentCart);
    
    // Actualizar localStorage
    localStorage.setItem('products_cart', JSON.stringify(cleanedCart));
    
    // Actualizar estado
    setState(prevState => ({
      ...prevState,
      cart: cleanedCart
    }));
    
    return cleanedCart;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');

    if (token) {
      fetchUser(token);
      fetchBillings(token);
    } else {
      // Si no hay token, limpiar el usuario del estado
      setState((prevState) => ({
        ...prevState,
        user: null,
        cartSummary: null,
      }));
      localStorage.removeItem('cart_summary');
      setLoading(false);
    }
  }, []);

  // Mixpanel (task-1635): une la navegación anónima de este navegador con el
  // usuario en cuanto hay sesión. Cubre login con correo o Google, registro y
  // sesión restaurada al cargar, porque todos terminan poniendo state.user.
  // Se exige el token para no identificar con un user_login viejo.
  const mixpanelUserId = state.user?.id;
  useEffect(() => {
    if (mixpanelUserId && localStorage.getItem('auth_token')) {
      identifyUser(state.user);
    }
  }, [mixpanelUserId]);

  // Logueado: al cambiar la comuna de despacho se recarga el carrito para que el
  // backend recalcule las reglas por comuna. La primera vez no, porque el carrito
  // ya se carga al montar.
  const comunaAnteriorRef = useRef(state.deliveryComuna);
  useEffect(() => {
    if (comunaAnteriorRef.current === state.deliveryComuna) return;
    comunaAnteriorRef.current = state.deliveryComuna;
    if (isAuthenticated && loadCartBackend) {
      loadCartBackend();
    }
  }, [state.deliveryComuna, isAuthenticated, loadCartBackend]);

  // Sincronizar carrito del backend con el estado local cuando cambie
  useEffect(() => {
    if (isAuthenticated && backendCart && backendCart.items) {
      const productsCart = backendCart.items.map(item => ({
        product_id: item.product.internal_id || item.product.id,
        wordpress_id: item.product.wordpress_id || item.product.id,
        sku: item.product.sku,
        name: item.product.name,
        slug: item.product.slug,
        price: item.product.price,
        sale_price: item.product.sale_price,
        image: getBestProductImage(item.product),
        description: item.product.description,
        quantity: item.quantity,
        tags: item.product.tags,
        stock_quantity: item.product.stock_quantity,
        stock_status: item.product.stock_status || "instock",
        total_sales: item.product.total_sales,
        box: item.product.box,
        categories: item.product.categories,
        // Mixpanel (task-1635): marca, distribuidor, tags y categorías del backend.
        analytics: item.product.analytics || null,
        cart_item_id: item.id,
        // Guardar información de cambio de precio
        price_changed: item.price_changed || false,
        price_change_info: item.price_change_info || null,
      }));
      
      const cleanedCart = cleanCartData(productsCart);
      const normalizedCartSummary = normalizeCartSummary(backendCart);
      setState(prevState => ({
        ...prevState,
        cart: cleanedCart,
        cartSummary: normalizedCartSummary,
      }));
      localStorage.setItem('products_cart', JSON.stringify(cleanedCart));
      localStorage.setItem('cart_summary', JSON.stringify(normalizedCartSummary));
    }
  }, [backendCart, isAuthenticated]);



  const fetchUser = async (token) => {
    try {
      const response = await fetch(process.env.BASE_URL + '/user', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Primero el estado (con pedidos y puntos) y después la copia liviana en
        // localStorage: si el almacenamiento falla, la página igual se actualiza
        // (task-1842).
        setState((prevState) => ({
          ...prevState,
          user: data,
        }));
        saveStoredUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillings = async (token) => {
    try {
      const response = await fetch(process.env.BASE_URL + '/billings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('billings', JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          billings: data,
        }));
      }
    } catch (error) {
      console.error('Error fetching billings:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(process.env.BASE_URL + '/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // El token primero: antes, si guardar el usuario fallaba, no quedaba la sesión.
        localStorage.setItem('auth_token', data.access_token);
        setState((prevState) => ({
          ...prevState,
          user: data.user,
        }));
        saveStoredUser(data.user);
        trackLogin(data.user, 'email'); // Mixpanel (task-1635)
        fetchUser(data.access_token); // Fetch billings after login
        // Cargar carrito del backend después del login
        if (loadCartBackend) {
          setTimeout(() => {
            loadCartBackend();
          }, 500);
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Mixpanel: el próximo que use este navegador empieza como anónimo y no
    // queda unido a esta cuenta (equipos compartidos).
    resetMixpanel();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_login'); // Agregar esta línea para limpiar user_login
    localStorage.removeItem('billings');
    localStorage.removeItem('discountAmount');
    localStorage.removeItem('checkoutData');
    localStorage.removeItem('address');
    localStorage.removeItem('products_cart');
    localStorage.removeItem('cart_summary');
    // La comuna era de una dirección de este usuario.
    localStorage.removeItem('delivery_comuna');
    setState((prevState) => ({
      ...prevState,
      user: null,
      billings: [], // Limpiar facturaciones al cerrar sesión
      cartSummary: null,
      deliveryComuna: null,
    }));
  };

  const addBilling = async (billingData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(process.env.BASE_URL + '/billings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(billingData)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('billings', JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          billings: [...prevState.billings, data],
        }));
      } else {
        throw new Error('Error al guardar los datos de facturación');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateBilling = async (id, billingData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(process.env.BASE_URL + `/billings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(billingData)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('billings', JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          billings: prevState.billings.map(billing => billing.id === id ? data : billing),
        }));
      } else {
        throw new Error('Error al actualizar los datos de facturación');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteBilling = async (id) => {
    try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(process.env.BASE_URL + `/billings/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.ok) {
            // Obtener los datos actuales del localStorage
            const billings = JSON.parse(localStorage.getItem('billings')) || [];

            // Filtrar el billing eliminado
            const updatedBillings = billings.filter(billing => billing.id !== id);

            // Actualizar el localStorage con los datos filtrados
            localStorage.setItem('billings', JSON.stringify(updatedBillings));

            // Actualizar el estado global
            setState((prevState) => ({
                ...prevState,
                billings: prevState.billings.filter(billing => billing.id !== id),
            }));
        } else {
            throw new Error('Error al eliminar los datos de facturación');
        }
      if (response.ok) {
        localStorage.setItem('billings', JSON.stringify(response.json()));
        setState((prevState) => ({
          ...prevState,
          billings: prevState.billings.filter(billing => billing.id !== id),
        }));
      } else {
        throw new Error('Error al eliminar los datos de facturación');
      }
    } catch (error) {
        console.error('Error:', error);
    }
};



  const updateAddress = async (id, addressesData) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(process.env.BASE_URL + `/addresses/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addressesData)
      });
  
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('address', JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          user: {
            ...prevState.user,
            addresses: prevState.user.addresses.map(address =>
              address.id === id ? data : address
            ),
          },
        }));
      } else {
        throw new Error('Error al actualizar la dirección');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  

  // Todos los botones de agregar (tarjeta, ficha, landing, repetir compra)
  // pasan por acá. El evento de Mixpanel sale sólo si de verdad se agregó, por
  // cualquiera de los dos caminos (carrito del backend o local).
  // addSource: desde dónde se agregó (el buscador lo pasa; si no, sale de la
  // página). Se toma al hacer clic: la página puede cambiar mientras responde
  // el backend.
  const addToCart = async (payload, { addSource } = {}) => {
    const origen = addSource || addSourceFromPage();
    const agregado = await agregarAlCarrito(payload);
    if (agregado) {
      trackAddedToCart(payload, { addSource: origen });
    }
    return agregado;
  };

  const agregarAlCarrito = async (payload) => {
    const product = payload;

    // Si el usuario está autenticado, usar el backend
    if (isAuthenticated && isCartAuthenticated) {
      try {
        // Buscar si el producto ya está en el carrito del backend
        const currentCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
        const existingProduct = currentCart.find(item => item.product_id === product.product_id);
        
        let result;
        if (existingProduct && existingProduct.cart_item_id) {
          // Actualizar cantidad si ya existe
          const newQuantity = existingProduct.quantity + product.quantity;
          result = await updateQuantityBackend(existingProduct.cart_item_id, newQuantity);
        } else {
          // Agregar nuevo producto
          result = await addToCartBackend(product.product_id, product.quantity);
        }

        if (result.success) {
          // Disparar animación de shake del carrito
          triggerCartShake();

          // El hook useCart ya sincroniza el estado, solo necesitamos actualizar checkoutData
          const orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
          const updatedCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
          orderData.products = updatedCart;
          localStorage.setItem("checkoutData", JSON.stringify(orderData));

          // Intentar aplicar cupón automático
          const cartTotal = updatedCart.reduce((total, item) => {
            const price = item.sale_price || item.price;
            return total + (Number(price) * item.quantity);
          }, 0);
          try {
            applyAutomaticCouponFrontendOnly(updatedCart, cartTotal);
          } catch (error) {
            console.error('Error al aplicar cupón automático:', error);
          }

          // Enviar evento a Brevo
          if (user?.email) {
            try {
              await sendCartUpdatedEvent();
            } catch (error) {
              console.error('Error al enviar evento a Brevo:', error);
            }
          }

          return true;
        } else {
          console.error('Error al agregar al carrito:', result.error);
          return false;
        }
      } catch (error) {
        console.error('Error al agregar al carrito del backend:', error);
        // Fallback a localStorage si falla el backend
      }
    }

    // Fallback: usar localStorage (para usuarios no autenticados o si falla el backend)
    const currentCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    const existingProductIndex = currentCart.findIndex(item => item.product_id === product.product_id);

    let updatedCart;
    if (existingProductIndex >= 0) {
      const updatedQuantity = currentCart[existingProductIndex].quantity + product.quantity;
      if (updatedQuantity > product.stock_quantity) {
        return false;
      }
      updatedCart = currentCart.map((item, index) => {
        if (index === existingProductIndex) {
          return { ...item, quantity: updatedQuantity };
        }
        return item;
      });
    } else {
      updatedCart = [...currentCart, {
        product_id: product.product_id,
        wordpress_id: product.wordpress_id,
        sku: product.sku,
        name: product.name,
        slug: product.slug,
        price: product.price,
        sale_price: product.sale_price,
        image: product.image,
        description: product.description,
        quantity: product.quantity,
        tags: product.tags,
        stock_quantity: product.stock_quantity,
        total_sales: product.total_sales,
        box: product.box,
        categories: product.categories,
        analytics: product.analytics || null
      }];
    }

    const cartTotal = updatedCart.reduce((total, item) => {
      const price = item.sale_price || item.price;
      return total + (Number(price) * item.quantity);
    }, 0);

    const cleanedCart = cleanCartData(updatedCart);
    
    setState(prevState => ({
      ...prevState,
      cart: cleanedCart
    }));
    localStorage.setItem('products_cart', JSON.stringify(cleanedCart));

    const orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
    orderData.products = updatedCart;
    localStorage.setItem("checkoutData", JSON.stringify(orderData));

    try {
      applyAutomaticCouponFrontendOnly(updatedCart, cartTotal);
    } catch (error) {
      console.error('Error al aplicar cupón automático:', error);
    }

    // Disparar animación de shake del carrito (fallback localStorage)
    triggerCartShake();

    if (isAuthenticated && user?.email) {
      try {
        await sendCartUpdatedEvent();
      } catch (error) {
        console.error('Error al enviar evento a Brevo:', error);
      }
    }

    return true;
  };

  // Nueva función para agregar múltiples productos al carrito
  const addMultipleToCart = async (products, { addSource } = {}) => {
    const origen = addSource || addSourceFromPage(); // para Mixpanel
    // Leer el carrito actual desde localStorage
    const currentCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    
    let updatedCart = [...currentCart];
    let successCount = 0;
    const agregados = []; // para Mixpanel
    
    // Procesar cada producto
    for (const product of products) {
      // Verificar si el producto ya está en el carrito
      const existingProductIndex = updatedCart.findIndex(item => item.product_id === product.product_id);
      
      if (existingProductIndex >= 0) {
        // Actualizar cantidad si el producto ya existe
        const updatedQuantity = updatedCart[existingProductIndex].quantity + product.quantity;
        
        if (updatedQuantity <= product.stock_quantity) {
          updatedCart[existingProductIndex].quantity = updatedQuantity;
          successCount++;
          agregados.push(product);
        }
      } else {
        // Agregar nuevo producto
        updatedCart.push({
          product_id: product.product_id,
          wordpress_id: product.wordpress_id,
          sku: product.sku,
          name: product.name,
          slug: product.slug,
          price: product.price,
          sale_price: product.sale_price,
          image: product.image,
          description: product.description,
          quantity: product.quantity,
          tags: product.tags,
          stock_quantity: product.stock_quantity,
          total_sales: product.total_sales,
          box: product.box,
          categories: product.categories,
          analytics: product.analytics || null
        });
        successCount++;
        agregados.push(product);
      }
    }
    
    // Calcular total del carrito
    const cartTotal = updatedCart.reduce((total, item) => {
      const price = item.sale_price || item.price;
      return total + (Number(price) * item.quantity);
    }, 0);
    
    // Limpiar objetos vacíos antes de guardar
    const cleanedCart = cleanCartData(updatedCart);
    
    // Actualizar estado y localStorage
    setState(prevState => ({
      ...prevState,
      cart: cleanedCart
    }));
    localStorage.setItem('products_cart', JSON.stringify(cleanedCart));
    
    // Actualizar checkoutData
    const orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
    orderData.products = updatedCart;
    localStorage.setItem("checkoutData", JSON.stringify(orderData));
    
    // Intentar aplicar cupón automático
    try {
      const couponResult = applyAutomaticCouponFrontendOnly(updatedCart, cartTotal);
    } catch (error) {
      console.error('Error al aplicar cupón automático:', error);
    }

    // Disparar animación de shake del carrito
    if (successCount > 0) {
      triggerCartShake();
    }

    // Mixpanel (task-1635): un evento por producto agregado ("Agregar los N").
    agregados.forEach((producto) => trackAddedToCart(producto, { addSource: origen, source: 'add_multiple' }));
    
    // Enviar evento a Brevo si el usuario está autenticado
    if (isAuthenticated && user?.email) {
      try {
        await sendCartUpdatedEvent();
      } catch (error) {
        console.error('Error al enviar evento a Brevo:', error);
      }
    }
    
    return successCount;
  };

  const modalCart = (payload) => {
    setCartModal({
      ...cartModal,
      payload,
    });
  };

  const modalMenuMobile = (payload) => {
    setMenuMobile({
      ...menuMobile,
      payload,
    });
  };

  const modalLogin = (payload) => {
    setLoginModal({
      ...loginModal,
      payload,
    });
  };

  const modalRegister = (payload) => {
    setRegisterModal({
      ...registerModal,
      payload,
    });
  };

  const removeFromCart = async (payload) => {
    // Si el usuario está autenticado y el producto tiene cart_item_id, usar el backend
    if (isAuthenticated && isCartAuthenticated && payload.cart_item_id) {
      try {
        const result = await removeFromCartBackend(payload.cart_item_id);
        if (result.success) {
          // El hook useCart ya sincroniza el estado
          const filterCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
          
          // Solo eliminar cupón automático, no manual
          const couponData = JSON.parse(localStorage.getItem("couponData"));
          if (couponData && !couponData.isManual) {
            window.localStorage.removeItem('coupon_discount');
            localStorage.removeItem('couponData');
          }

          // Actualiza checkoutData
          let orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
          orderData.products = filterCart;
          localStorage.setItem("checkoutData", JSON.stringify(orderData));

          // Enviar evento a Brevo
          if (user?.email) {
            if (filterCart.length === 0) {
              sendCartDeletedEvent(payload);
            } else {
              sendCartUpdatedEvent();
            }
          }
          return;
        }
      } catch (error) {
        console.error('Error al eliminar del carrito del backend:', error);
        // Continuar con el fallback
      }
    }

    // Fallback: usar localStorage
    const newCart = [...state.cart];
    const filterCart = newCart.filter(items => items.product_id !== payload.product_id);
    
    const cleanedCart = cleanCartData(filterCart);
    
    localStorage.setItem("products_cart", JSON.stringify(cleanedCart));
    setState({
      ...state,
      cart: cleanedCart,
    });
    
    const couponData = JSON.parse(localStorage.getItem("couponData"));
    if (couponData && !couponData.isManual) {
      window.localStorage.removeItem('coupon_discount');
      localStorage.removeItem('couponData');
    }

    let orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
    orderData.products = filterCart;
    localStorage.setItem("checkoutData", JSON.stringify(orderData));

    if (isAuthenticated && user?.email) {
        if (filterCart.length === 0) {
            sendCartDeletedEvent(payload);
        } else {
            sendCartUpdatedEvent();
        }
    }
  };

  const removeFromCartMultiple = async (payloads) => {
    if (!payloads || payloads.length === 0) return;

    const idsToRemove = payloads.map(payload => payload.product_id);
    let newCart;

    // Logueado: el carrito vive en el backend y al recargar la página manda lo que
    // tiene él. Borrar solo en localStorage hacía que los productos volvieran
    // (task-1942).
    if (isAuthenticated && isCartAuthenticated && payloads.some(payload => payload.cart_item_id)) {
      const eliminaTodo = state.cart.every(item => idsToRemove.includes(item.product_id));
      let fallo = false;

      if (eliminaTodo) {
        const result = await clearCartBackend();
        fallo = !result.success;
      } else {
        // De a uno: cada respuesta trae el carrito completo y la última es la que
        // queda pintada. En paralelo, una respuesta atrasada podía volver a mostrar
        // un producto que otra petición ya había borrado.
        for (const payload of payloads) {
          if (!payload.cart_item_id) continue;
          const result = await removeFromCartBackend(payload.cart_item_id);
          if (!result.success) fallo = true;
        }
      }

      // Si alguna falló, se recarga para mostrar lo que de verdad quedó en el
      // backend y no un carrito que cambia al recargar la página.
      if (fallo) {
        await loadCartBackend();
      }

      // El hook useCart ya sincronizó el estado y localStorage con la respuesta.
      newCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    } else {
      // Limpiar objetos vacíos antes de guardar
      newCart = cleanCartData(state.cart.filter(item => !idsToRemove.includes(item.product_id)));

      localStorage.setItem("products_cart", JSON.stringify(newCart));
      setState({
        ...state,
        cart: newCart,
      });
    }

    // Solo eliminar cupón automático, no manual
    const couponData = JSON.parse(localStorage.getItem("couponData"));
    if (couponData && !couponData.isManual) {
      window.localStorage.removeItem('coupon_discount');
      localStorage.removeItem('couponData');
    }

    // Actualiza checkoutData si existe
    let orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
    orderData.products = newCart;
    localStorage.setItem("checkoutData", JSON.stringify(orderData));

    // Enviar evento a Brevo si el usuario está autenticado
    if (isAuthenticated && user?.email) {
        if (newCart.length === 0) {
            // Enviar el último producto eliminado como referencia
            sendCartDeletedEvent(payloads[payloads.length - 1]);
        } else {
            sendCartUpdatedEvent();
        }
    }
  };

  const getProductBySlug = (payload) => {
    setState({
      ...state,
      product: [...state.product, payload],
    });
  };

  const addCheckoutData = (checkoutData) => {
    setState({
        ...state,
        checkoutData: checkoutData, // Guardamos los datos en el estado global
    });
};


  const addCouponData = (payload) => {
    setState({
      ...state,
      couponData: payload,
    });
    localStorage.setItem("all_data_coupon", JSON.stringify(payload));
  };

  const addCouponDiscount = (payload) => {
    setState({
      ...state,
      couponDiscount: payload,
    });
    localStorage.setItem("coupon_discount", JSON.stringify(payload));
  };

  const addUserLogin = (payload) => {
    setState({
      ...state,
      user: payload,
    });
    saveStoredUser(payload, ['user_login']);
  };

  const addShippingCost = (payload) => {
    const shippingCostAsInteger = Number(Number(payload.total).toFixed(0));
    setState({
      ...state,
      shippingLines: {
        title: payload.title,
        total: shippingCostAsInteger,
      },
    });
  };

  const addProductSearched = (payload) => {
    setState({
      ...state,
      productSearched: payload,
    });
  };

  const refreshCartSummary = useCallback(async () => {
    if (!isAuthenticated || !loadCartBackend) {
      return null;
    }

    await loadCartBackend();
    return true;
  }, [isAuthenticated, loadCartBackend]);

  const updateStockInCart = async (payload) => {
    try {
      // Si el usuario está autenticado y el producto tiene cart_item_id, usar el backend
      if (isAuthenticated && isCartAuthenticated) {
        const currentCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
        const productToUpdate = currentCart.find(item => item.product_id === payload.product_id);
        
        if (productToUpdate && productToUpdate.cart_item_id) {
          // Validar stock
          if (payload.quantity > (productToUpdate.stock_quantity || payload.stock_quantity)) {
            console.log(`No puedes agregar más de ${productToUpdate.stock_quantity} unidades de ${productToUpdate.name}`);
            return false;
          }

          // Si la cantidad es 0, eliminar del carrito
          if (payload.quantity === 0) {
            const result = await removeFromCartBackend(productToUpdate.cart_item_id);
            if (result.success) {
              // El hook useCart ya sincroniza el estado
              const updatedCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
              const cartTotal = updatedCart.reduce((total, item) => {
                const price = item.sale_price || item.price;
                return total + (Number(price) * item.quantity);
              }, 0);

              try {
                validateAndRemoveCoupon(updatedCart, cartTotal);
              } catch (error) {
                console.error('Error al validar cupón automático:', error);
              }

              if (user?.email) {
                if (updatedCart.length === 0) {
                  await sendCartDeletedEvent(productToUpdate);
                } else {
                  await sendCartUpdatedEvent();
                }
              }
              return true;
            }
          } else {
            // Actualizar cantidad
            const result = await updateQuantityBackend(productToUpdate.cart_item_id, payload.quantity);
            if (result.success) {
              // El hook useCart ya sincroniza el estado
              const updatedCart = JSON.parse(localStorage.getItem('products_cart') || '[]');
              const cartTotal = updatedCart.reduce((total, item) => {
                const price = item.sale_price || item.price;
                return total + (Number(price) * item.quantity);
              }, 0);

              let orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
              orderData.products = updatedCart;
              localStorage.setItem("checkoutData", JSON.stringify(orderData));

              try {
                const isCurrentCouponValid = validateAndRemoveCoupon(updatedCart, cartTotal);
                if (!isCurrentCouponValid) {
                  applyAutomaticCouponFrontendOnly(updatedCart, cartTotal);
                }
              } catch (error) {
                console.error('Error al validar cupón automático:', error);
              }

              if (user?.email) {
                await sendCartUpdatedEvent();
              }
              return true;
            }
          }
        }
      }

      // Fallback: usar localStorage
      const updatedCart = [...state.cart];
      const productToUpdate = updatedCart.find(item => item.product_id === payload.product_id);

      if (productToUpdate) {
        if (payload.quantity > (productToUpdate.stock_quantity || payload.stock_quantity)) {
          console.log(`No puedes agregar más de ${productToUpdate.stock_quantity} unidades de ${productToUpdate.name}`);
          return false;
        }

        productToUpdate.quantity = payload.quantity;

        if (productToUpdate.quantity === 0) {
          const index = updatedCart.findIndex(item => item.product_id === payload.product_id);
          updatedCart.splice(index, 1);
        }
      }

      const cartTotal = updatedCart.reduce((total, item) => {
        const price = item.sale_price || item.price;
        return total + (Number(price) * item.quantity);
      }, 0);

      setState({
        ...state,
        cart: updatedCart,
      });

      localStorage.setItem("products_cart", JSON.stringify(updatedCart));

      let orderData = JSON.parse(localStorage.getItem("checkoutData")) || {};
      orderData.products = updatedCart;
      localStorage.setItem("checkoutData", JSON.stringify(orderData));

      try {
        const isCurrentCouponValid = validateAndRemoveCoupon(updatedCart, cartTotal);
        if (!isCurrentCouponValid) {
          applyAutomaticCouponFrontendOnly(updatedCart, cartTotal);
        }
      } catch (error) {
        console.error('Error al validar cupón automático:', error);
      }

      if (isAuthenticated && user?.email) {
        if (updatedCart.length === 0) {
          await sendCartDeletedEvent(productToUpdate);
        } else {
          await sendCartUpdatedEvent();
        }
      }

      return true;
    } catch (error) {
      console.error('Error al actualizar el stock:', error);
      return false;
    }
  };




  return {
    state,
    cartModal,
    loginModal,
    registerModal,
    menuMobile,
    loading,
    addToCart,
    addMultipleToCart, // Nueva función para agregar múltiples productos
    modalCart,
    removeFromCart,
    getProductBySlug,
    modalLogin,
    modalRegister,
    addCheckoutData,
    addUserLogin,
    addShippingCost,
    addCouponData,
    addCouponDiscount,
    addProductSearched,
    modalMenuMobile,
    updateStockInCart,
    removeFromCartMultiple,
    refreshCartSummary,
    setDeliveryComuna, // Comuna de despacho para las reglas por comuna (task-1379)
    login,
    logout,
    addBilling,
    updateBilling,
    deleteBilling,
    updateAddress,
    cleanCart, // Función para limpiar objetos vacíos del carrito
    revalidateCartPrices, // Revalida precios del carrito (no logueados)
    setState, // Asegúrate de exportar setState
    triggerCartShake, // Función para disparar la animación de shake del carrito
  };
};

export default useInitialState;